import { api, tokens } from "../lib/api.js";

/**
 * Videos people paid to get on the stream.
 *
 * Two jobs on one page, because they are two halves of the same decision: what
 * you charge, and what you have agreed to put on your own broadcast.
 *
 * The review half is the one that matters. Approving without watching is not
 * reviewing, and the streamer is the only safeguard between a stranger's video
 * and their audience — so the player is on the page, not behind a download, and
 * the buttons sit under it rather than in the list.
 *
 * No money is handled here or anywhere else in Stream-Cut. The payment happened
 * on the streamer's own tip page; this page knows only that it happened. That is
 * also why a rejection raises a refund the streamer owes rather than issuing
 * one: we could not issue it if we wanted to.
 */

const esc = (v: string) =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

type Kind = "reaction" | "promotion" | "both";

const KINDS: Kind[] = ["reaction", "promotion", "both"];

/** What each kind is called where the streamer reads it. */
const KIND_LABEL: Record<Kind, string> = {
  reaction: "React to my video",
  promotion: "Promote my product",
  both: "React to my video and promote it",
};

/** And what it commits them to, which is the part worth being explicit about. */
const KIND_NOTE: Record<Kind, string> = {
  reaction: "Their video joins your queue and you react to it on stream.",
  promotion:
    "Their link goes in your promotion rotation for one stream, with a button " +
    "on your viewers' phones. Labelled as a paid promotion, always.",
  both: "Both: you react to their video, and their link goes in your rotation.",
};

interface Tier {
  max_seconds: number;
  price_cents: number;
  kind?: Kind;
}

/**
 * A ladder to start from, if the streamer wants one.
 *
 * Offered as a button and never applied on its own. It used to be the column
 * default, which meant every account was created already priced -- for an
 * audience we cannot see, in a market we know nothing about, by somebody with
 * no stake in the answer. A price is the one thing here that is nobody's
 * business but the streamer's.
 *
 * The numbers are a plausible shape rather than a recommendation: a cheap
 * short one, a middle, and a promotion priced above the reactions because it
 * asks more of the streamer. Whoever presses the button will change them, and
 * that is the point -- it fills a form they are looking at rather than a
 * database they are not.
 */
const SUGGESTED: Tier[] = [
  { price_cents: 500, max_seconds: 60, kind: "reaction" },
  { price_cents: 1200, max_seconds: 120, kind: "reaction" },
  { price_cents: 1500, max_seconds: 60, kind: "promotion" },
];

interface Settings {
  submissions_open: boolean;
  submission_url: string | null;
  tiers: Tier[];
  rails: {
    kofi: { connected: boolean; webhook_url: string };
    streamelements: { connected: boolean; webhook_url: string };
    buymeacoffee: { connected: boolean; webhook_url: string };
  };
  limits: { min_seconds: number; max_seconds: number; max_mb: number };
}

interface Submission {
  id: string;
  state: string;
  kind: Kind;
  promo_label: string | null;
  promo_link: string | null;
  submitter_handle: string;
  amount_cents: number;
  currency: string;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  reject_reason: string | null;
  refund_owed: boolean;
  created_at: string;
  expires_at: string | null;
}

const money = (cents: number, currency: string) =>
  `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;

const secs = (ms: number | null) => (ms === null ? "—" : `${Math.round(ms / 1000)}s`);

/**
 * What each state means to the streamer, in their terms.
 *
 * The enum is written from the system's point of view — `awaiting_upload`,
 * `pending_review` — and a streamer should not have to learn it to use the
 * page. "Waiting for their video" says the same thing and says whose turn it is.
 */
const STATE: Record<string, string> = {
  paid: "Paid — we could not email them",
  awaiting_upload: "Waiting for their video",
  validating: "Checking their file",
  pending_review: "Yours to review",
  approved: "Approved — plays next stream",
  played: "Played",
  rejected: "Rejected",
  expired: "Expired",
  withdrawn: "Withdrawn",
};

export async function requestsView(root: HTMLElement) {
  root.innerHTML = `<header class="page-head"><h1>Paid videos</h1></header>
    <div id="reqBody" class="loading">Loading…</div>`;

  const body = root.querySelector<HTMLElement>("#reqBody")!;

  let settings: Settings;
  let items: Submission[];
  try {
    [settings, items] = await Promise.all([
      api<Settings>("GET", "/v1/submissions/settings"),
      api<{ items: Submission[] }>("GET", "/v1/submissions").then((r) => r.items),
    ]);
  } catch (err) {
    body.classList.remove("loading");
    body.textContent = err instanceof Error ? err.message : String(err);
    return;
  }

  body.classList.remove("loading");

  function render() {
    const waiting = items.filter((i) => i.state === "pending_review");
    const rest = items.filter((i) => i.state !== "pending_review");
    const owed = items.filter((i) => i.refund_owed);

    body.innerHTML = `
      ${
        owed.length
          ? `<div class="notice warn">
               <strong>${owed.length} refund${owed.length === 1 ? "" : "s"} to make.</strong>
               The money is in your own tip account, so only you can return it.
               Refund these on the page they paid on:
               ${esc(owed.map((o) => `${o.submitter_handle} (${money(o.amount_cents, o.currency)})`).join(", "))}
             </div>`
          : ""
      }

      <section class="card">
        <h2>How it works</h2>
        <p class="meta">Someone pays you on your own Ko-fi or StreamElements page.
           Stream-Cut emails them a link to upload their video, checks the file will
           actually play, and brings it here for you to approve. Approved videos join
           your Studio queue and are fetched before you go live. You decide what
           plays; nothing reaches your stream without you watching it first.</p>
        <p class="meta"><strong>We never touch the money.</strong> It goes straight to
           your tip account. If you turn a video down, refunding is yours to do — this
           page keeps the list.</p>
      </section>

      <section class="card">
        <h2>Yours to review${waiting.length ? ` · ${waiting.length}` : ""}</h2>
        ${
          waiting.length === 0
            ? `<p class="meta">Nothing waiting.</p>`
            : waiting
                .map(
                  (s) => `<div class="req" data-id="${esc(s.id)}">
              <div class="req-head">
                <strong>${esc(s.submitter_handle)}</strong>
                <span class="req-kind">${esc(KIND_LABEL[s.kind] ?? s.kind)}</span>
                <span class="meta">${money(s.amount_cents, s.currency)}${
                  s.duration_ms ? ` · ${secs(s.duration_ms)}` : ""
                }${s.width && s.height ? ` · ${s.width}×${s.height}` : ""}</span>
              </div>
              <p class="meta">${esc(KIND_NOTE[s.kind] ?? "")}</p>
              ${
                s.promo_link
                  ? `<p class="req-link">Button: <strong>${esc(s.promo_label ?? "")}</strong>
                       → <a href="${esc(s.promo_link)}" target="_blank"
                            rel="noopener noreferrer nofollow">${esc(s.promo_link)}</a>
                       <span class="meta">Open it before you accept — it goes on your
                       viewers' phones under your name.</span></p>`
                  : ""
              }
              ${
                // A promotion can be sold without one, and an empty player on a
                // card would read as a video that failed to load.
                s.duration_ms || s.kind !== "promotion"
                  ? `<video class="req-video" preload="metadata" controls
                            data-video="${esc(s.id)}"></video>`
                  : ""
              }
              <div class="req-actions">
                <button class="btn" data-approve>Approve — plays next stream</button>
                <input type="text" data-reason maxlength="300"
                       placeholder="Why not? They'll be told.">
                <button class="btn" data-reject>Turn down &amp; refund</button>
              </div>
              <p class="meta" data-note></p>
            </div>`,
                )
                .join("")
        }
      </section>

      <section class="card">
        <h2>What you charge</h2>
        <p class="meta">Your prices, which we never set: Stream-Cut takes no cut and
           cannot see your audience, so it has no business picking a number. A payment
           buys the most generous tier its amount covers, and that is also what decides
           <em>which</em> of the three it is — so no two tiers can share a price.
           Anything under your cheapest tier is an ordinary tip and asks nothing of
           you.</p>
        <p class="meta">A tier can be priced at anything from a penny upwards, and a
           cheap rung is usually how people find out the feature exists. Two things to
           weigh before going very low: your tip service and its card processor take a
           percentage <em>and</em> a fixed fee per payment, which at a dollar is a
           large share of it — and your own time per submission is the same whatever
           was paid, because you still watch it and decide.</p>
        <label class="row">
          <span>Accepting videos</span>
          <select id="reqOpen">
            <option value="1"${settings.submissions_open ? " selected" : ""}>Open</option>
            <option value="0"${!settings.submissions_open ? " selected" : ""}>Closed</option>
          </select>
        </label>
        <label class="row">
          <span>Your pay page</span>
          <input type="url" id="reqPayUrl" placeholder="https://ko-fi.com/yourname"
                 value="${esc(settings.submission_url ?? "")}">
        </label>
        <p class="hint">Paste your own Ko-fi or StreamElements page. Viewers get a
           button to it on the same page they tap reactions on, which is the only
           place we know they are &mdash; without it the feature is reachable only
           by somebody who already knows your address. The button shows only while
           you are open and this is filled in, so clearing it takes the button
           down without closing the queue.</p>
        <div id="tiers"></div>
        <div class="actions">
          <button class="btn small" id="tierAdd">Add a tier</button>
          ${
            // Only when there is nothing to lose. Offering it over a ladder
            // somebody has already set is offering to overwrite their prices.
            draft.length === 0
              ? `<button class="btn small" id="tierSuggest">Start from an example</button>`
              : ""
          }
          <button class="btn" id="tierSave">Save</button>
        </div>
        <p class="meta" id="tierNote">Between ${settings.limits.min_seconds} and
           ${settings.limits.max_seconds} seconds, up to ${settings.limits.max_mb} MB.</p>
      </section>

      <section class="card">
        <h2>Where the payments come from</h2>
        <p class="meta">Paste the address below into your tip service as its webhook,
           and put the same service's own token or secret here. It lets us check that a
           payment notification really came from them — it gives us no access to your
           account and cannot move money.</p>
        ${rail("kofi", "Ko-fi", settings.rails.kofi)}
        ${rail("buymeacoffee", "Buy Me a Coffee", settings.rails.buymeacoffee)}
        ${rail("streamelements", "StreamElements", settings.rails.streamelements)}
      </section>

      ${
        rest.length
          ? `<section class="card">
               <h2>Everything else</h2>
               <table class="keytable">
                 <tbody>${rest
                   .map(
                     (s) => `<tr>
                       <td>${esc(s.submitter_handle)}</td>
                       <td>${money(s.amount_cents, s.currency)}</td>
                       <td>${esc(STATE[s.state] ?? s.state)}</td>
                       <td class="meta">${esc(s.reject_reason ?? "")}</td>
                     </tr>`,
                   )
                   .join("")}</tbody>
               </table>
             </section>`
          : ""
      }`;

    renderTiers();
    loadVideos();
  }

  function rail(
    id: string,
    label: string,
    state: { connected: boolean; webhook_url: string },
  ): string {
    return `<div class="rail" data-rail="${id}">
        <div class="req-head">
          <strong>${esc(label)}</strong>
          <span class="meta">${state.connected ? "Connected" : "Not connected"}</span>
        </div>
        <label class="row">
          <span>Webhook address</span>
          <input type="text" readonly value="${esc(state.webhook_url)}" data-url>
        </label>
        <div class="actions">
          <input type="password" data-secret autocomplete="off"
                 placeholder="${state.connected ? "Replace the token" : `Your ${esc(label)} token`}">
          <button class="btn small" data-connect>${state.connected ? "Replace" : "Connect"}</button>
          ${state.connected ? `<button class="btn small" data-disconnect>Disconnect</button>` : ""}
        </div>
      </div>`;
  }

  /** The ladder, edited in place. Kept in a local array until Save. */
  let draft: Tier[] = settings.tiers.map((t) => ({ ...t }));

  function renderTiers() {
    const host = body.querySelector<HTMLElement>("#tiers");
    if (!host) return;
    host.innerHTML = draft.length
      ? draft
          .map(
            (t, i) => `<div class="row tier" data-i="${i}">
              <input type="number" data-price min="1" step="1" value="${(t.price_cents / 100).toFixed(2)}">
              <span class="meta">buys</span>
              <select data-kind>
                ${KINDS.map(
                  (k) =>
                    `<option value="${k}"${(t.kind ?? "reaction") === k ? " selected" : ""}>${esc(
                      KIND_LABEL[k],
                    )}</option>`,
                ).join("")}
              </select>
              <span class="meta">up to</span>
              <input type="number" data-secs min="${settings.limits.min_seconds}"
                     max="${settings.limits.max_seconds}" step="1" value="${t.max_seconds}">
              <span class="meta">seconds of video</span>
              <button class="btn small" data-drop>Remove</button>
            </div>`,
          )
          .join("")
      : `<p class="meta">No tiers yet, so nothing is being sold.</p>`;
  }

  /**
   * Loads each video lazily, one signed URL at a time.
   *
   * `preload="metadata"` on the element and a URL fetched per card: minting one
   * URL for every submission on the page would hand out access to videos nobody
   * opened, and preloading them would pull several hundred megabytes through a
   * dashboard somebody came to for one decision.
   */
  function loadVideos() {
    body.querySelectorAll<HTMLVideoElement>("[data-video]").forEach((el) => {
      const id = el.dataset.video!;
      api<{ url: string }>("GET", `/v1/submissions/${id}/video`)
        .then((r) => {
          el.src = r.url;
        })
        .catch(() => {
          el.replaceWith(
            Object.assign(document.createElement("p"), {
              className: "meta",
              textContent: "That video could not be loaded. Turning it down refunds them.",
            }),
          );
        });
    });
  }

  const note = (el: Element | null, text: string) => {
    const p = el?.querySelector<HTMLElement>("[data-note]");
    if (p) p.textContent = text;
  };

  // Delegated: every section is re-rendered after an action, and handlers bound
  // to replaced nodes stop working silently.
  body.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    const card = target.closest<HTMLElement>(".req");
    if (card && (target.closest("[data-approve]") || target.closest("[data-reject]"))) {
      const id = card.dataset.id!;
      const rejecting = !!target.closest("[data-reject]");
      card.querySelectorAll("button").forEach((b) => (b.disabled = true));
      note(card, rejecting ? "Turning it down…" : "Approving…");
      try {
        if (rejecting) {
          const reason = card.querySelector<HTMLInputElement>("[data-reason]")?.value ?? "";
          await api("POST", `/v1/submissions/${id}/reject`, { reason });
        } else {
          await api("POST", `/v1/submissions/${id}/approve`);
        }
        items = await api<{ items: Submission[] }>("GET", "/v1/submissions").then((r) => r.items);
        render();
      } catch (err) {
        card.querySelectorAll("button").forEach((b) => (b.disabled = false));
        note(card, err instanceof Error ? err.message : String(err));
      }
      return;
    }

    if (target.closest("[data-drop]")) {
      const i = Number(target.closest<HTMLElement>(".tier")?.dataset.i);
      draft = draft.filter((_, at) => at !== i);
      renderTiers();
      return;
    }

    if (target.closest("#tierSuggest")) {
      // Into the form, not into the database. They still have to press Save,
      // which is what makes the prices theirs.
      draft = SUGGESTED.map((t) => ({ ...t }));
      render();
      const noteEl = body.querySelector<HTMLElement>("#tierNote");
      if (noteEl) {
        noteEl.textContent =
          "An example to change. Nothing is for sale until you press Save.";
      }
      return;
    }

    if (target.closest("#tierAdd")) {
      // A sensible next rung rather than an empty row: the commonest edit is
      // "one more, a bit dearer, a bit longer".
      const last = draft[draft.length - 1];
      draft = [
        ...draft,
        last
          ? {
              price_cents: last.price_cents * 2,
              max_seconds: Math.min(settings.limits.max_seconds, last.max_seconds * 2),
            }
          : { price_cents: 300, max_seconds: Math.max(settings.limits.min_seconds, 30) },
      ];
      renderTiers();
      return;
    }

    if (target.closest("#tierSave")) {
      const rows = [...body.querySelectorAll<HTMLElement>(".tier")];
      const tiers: Tier[] = rows.map((r) => ({
        price_cents: Math.round(
          Number(r.querySelector<HTMLInputElement>("[data-price]")!.value) * 100,
        ),
        max_seconds: Math.round(Number(r.querySelector<HTMLInputElement>("[data-secs]")!.value)),
        kind: (r.querySelector<HTMLSelectElement>("[data-kind]")!.value as Kind) ?? "reaction",
      }));
      const open = body.querySelector<HTMLSelectElement>("#reqOpen")!.value === "1";
      const noteEl = body.querySelector<HTMLElement>("#tierNote")!;
      noteEl.textContent = "Saving…";
      try {
        const payUrl = body.querySelector<HTMLInputElement>("#reqPayUrl")!.value.trim();
        await api("PUT", "/v1/submissions/settings", {
          submission_tiers: tiers,
          submissions_open: open,
          submission_url: payUrl,
        });
        draft = tiers;
        settings = { ...settings, tiers, submissions_open: open, submission_url: payUrl || null };
        noteEl.textContent = !open
          ? "Saved, and closed to new videos."
          : payUrl
            ? `Saved. ${tiers.length} tier${tiers.length === 1 ? "" : "s"}, and viewers get a button to your pay page.`
            : `Saved. ${tiers.length} tier${tiers.length === 1 ? "" : "s"}, but with no pay page your viewers have no way to reach it.`;
      } catch (err) {
        // The API writes these messages for the streamer, so they are shown as
        // given: every one of them names the tier that is wrong and why.
        noteEl.textContent = err instanceof Error ? err.message : String(err);
      }
      return;
    }

    const railBox = target.closest<HTMLElement>(".rail");
    if (railBox && (target.closest("[data-connect]") || target.closest("[data-disconnect]"))) {
      const which = railBox.dataset.rail!;
      // Named rather than branched on two values: a third rail turned the
      // ternary into the kind of expression where adding a fourth quietly
      // sends its secret to the wrong column.
      const FIELD: Record<string, string> = {
        kofi: "kofi_token",
        buymeacoffee: "buymeacoffee_secret",
        streamelements: "streamelements_secret",
      };
      const field = FIELD[which ?? ""];
      if (!field) return;
      const disconnecting = !!target.closest("[data-disconnect]");
      const input = railBox.querySelector<HTMLInputElement>("[data-secret]")!;
      const value = disconnecting ? "" : input.value.trim();
      if (!disconnecting && !value) return;

      try {
        await api("PUT", "/v1/submissions/settings", { [field]: value });
        // Cleared rather than left in the field. It is a secret and the page
        // has no reason to keep holding it.
        input.value = "";
        settings = await api<Settings>("GET", "/v1/submissions/settings");
        render();
      } catch (err) {
        railBox.querySelector<HTMLElement>(".meta")!.textContent =
          err instanceof Error ? err.message : String(err);
      }
    }
  });

  // Copying the webhook address is the whole setup step, so clicking it selects
  // it rather than requiring a careful drag across a long URL.
  body.addEventListener("focusin", (e) => {
    const el = e.target as HTMLInputElement;
    if (el.matches("[data-url]")) el.select();
  });

  render();
  void tokens;
}
